import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';

// Lazy load pages
const Home = React.lazy(() => import('./pages/Home'));
const About = React.lazy(() => import('./pages/About'));
const Services = React.lazy(() => import('./pages/Services'));
const Portfolio = React.lazy(() => import('./pages/Portfolio'));
const Testimonials = React.lazy(() => import('./pages/Testimonials'));
const Products = React.lazy(() => import('./pages/Products'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogDetail = React.lazy(() => import('./pages/BlogDetail'));
const ProjectDetail = React.lazy(() => import('./pages/ProjectDetail'));
const HelpCenter = React.lazy(() => import('./pages/HelpCenter'));
const Awards = React.lazy(() => import('./pages/Awards'));
const Media = React.lazy(() => import('./pages/Media'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const Terms = React.lazy(() => import('./pages/Terms'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

// Admin Pages
const AdminLogin = React.lazy(() => import('./pages/admin/Login'));
const AdminLayout = React.lazy(() => import('./pages/admin/AdminLayout'));
const Dashboard = React.lazy(() => import('./pages/admin/Dashboard'));
const ManageTeam = React.lazy(() => import('./pages/admin/ManageTeam'));
const ManagePortfolio = React.lazy(() => import('./pages/admin/ManagePortfolio'));
const ManageBlog = React.lazy(() => import('./pages/admin/ManageBlog'));
const ManageTestimonials = React.lazy(() => import('./pages/admin/ManageTestimonials'));
const ManageVideoTestimonials = React.lazy(() => import('./pages/admin/ManageVideoTestimonials'));
const ManageCaseStudies = React.lazy(() => import('./pages/admin/ManageCaseStudies'));
const ManagePartnerEndorsements = React.lazy(() => import('./pages/admin/ManagePartnerEndorsements'));
const ManageProducts = React.lazy(() => import('./pages/admin/ManageProducts'));
const ManageAwards = React.lazy(() => import('./pages/admin/ManageAwards'));
const ManageMedia = React.lazy(() => import('./pages/admin/ManageMedia'));
const Messages = React.lazy(() => import('./pages/admin/Messages'));
const Analytics = React.lazy(() => import('./pages/admin/Analytics'));
const Settings = React.lazy(() => import('./pages/admin/Settings'));
const UserManagement = React.lazy(() => import('./pages/admin/UserManagement'));

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-slate-50">
    <div className="relative">
      <div className="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
      <div className="absolute top-0 left-0 w-16 h-16 border-4 border-primary-600 rounded-full border-t-transparent animate-spin"></div>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/system-management-portal/login" replace />;
};

const App = () => {
  return (
    <AuthProvider>
      <DataProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <React.Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Layout><Home /></Layout>} />
              <Route path="/about" element={<Layout><About /></Layout>} />
              <Route path="/services" element={<Layout><Services /></Layout>} />
              <Route path="/products" element={<Layout><Products /></Layout>} />
              <Route path="/portfolio" element={<Layout><Portfolio /></Layout>} />
              <Route path="/portfolio/:id" element={<Layout><ProjectDetail /></Layout>} />
              <Route path="/testimonials" element={<Layout><Testimonials /></Layout>} />
              <Route path="/blog" element={<Layout><Blog /></Layout>} />
              <Route path="/blog/:id" element={<Layout><BlogDetail /></Layout>} />
              <Route path="/help" element={<Layout><HelpCenter /></Layout>} />
              <Route path="/awards" element={<Layout><Awards /></Layout>} />
              <Route path="/media" element={<Layout><Media /></Layout>} />
              <Route path="/contact" element={<Layout><Contact /></Layout>} />
              <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
              <Route path="/terms" element={<Layout><Terms /></Layout>} />
              
              {/* Admin Routes */}
              <Route path="/system-management-portal/login" element={<AdminLogin />} />
              <Route 
                path="/system-management-portal" 
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="team" element={<ManageTeam />} />
                <Route path="portfolio" element={<ManagePortfolio />} />
                <Route path="blog" element={<ManageBlog />} />
                <Route path="testimonials" element={<ManageTestimonials />} />
                <Route path="video-testimonials" element={<ManageVideoTestimonials />} />
                <Route path="case-studies" element={<ManageCaseStudies />} />
                <Route path="partner-endorsements" element={<ManagePartnerEndorsements />} />
                <Route path="awards" element={<ManageAwards />} />
                <Route path="media" element={<ManageMedia />} />
                <Route path="products" element={<ManageProducts />} />
                <Route path="messages" element={<Messages />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="settings" element={<Settings />} />
                <Route path="users" element={<UserManagement />} />
              </Route>
              
              {/* 404 Route */}
              <Route path="*" element={<Layout><NotFound /></Layout>} />
            </Routes>
          </React.Suspense>
        </Router>
      </DataProvider>
    </AuthProvider>
  );
};

export default App;

